'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Neden kurduk" — beyaz zeminde üç sahnelik anlatı.
 *
 * Koyu sayfa başlığının hemen altındaki ilk açık bölüm; anasayfada bu yeri
 * Platforms tutuyor. Yerleşim SSS ile aynı iskelet (solda sabitlenen başlık
 * bloğu, sağda liste) — aynı sitede iki farklı "başlık + liste" düzeni olması
 * için sebep yok, üstelik dikey hatlar da böylece aynı sütuna düşüyor.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Metin bir SIRA anlatıyor: sorun → eksik → yaptığımız şey. Hareketin tek işi
 * bunu görünür kılmak. Sol kenardaki hat, sahne girdikçe yukarıdan aşağı
 * çiziliyor (`scaleY`, transform-origin: top) ve bir sonraki düğüme bağlanıyor.
 * Yani çizgi "buradan oraya gidiliyor" diyor; süs olsaydı hepsi aynı anda
 * belirirdi.
 *
 * Son sahnenin altında hat YOK — dizi orada bitiyor. Kapanış cümlesi bunun
 * yerine kendi yeşil işaretini alıyor: hattın vardığı yer o cümle.
 *
 * Blur yalnızca sahne metinlerinde ve kapanışta (toplam 4 öğe; §6'daki ~15
 * bütçesinin içinde). Başlıklar ve numaralar blursuz — hepsi bulanıklaşırsa
 * "odaklanma" hissi kaybolur, bölüm sadece puslu açılır.
 *
 * Döngü ve parallax yok, elle kapatılacak hareket kalmıyor (DESIGN.md §1).
 *
 * Giriş hareketi YOK (kullanıcı kararı, 9 Eyl 2026): bölüm sayfa açılır
 * açılmaz son hâlinde basılıyor (`initial={false}`). Eskiden `whileInView` +
 * `once: true` ile kaydırdıkça açılıyordu; site sahipleri sayfanın yüklenmiş
 * gelmesini istedi. Variant'lar yerinde — geri istenirse tetik tek satır.
 */

/** Sahne sırası; numaralar (01…04) buradan türüyor. */
const BEATS = ['problem', 'gap', 'answer', 'today'] as const;

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

/* Sol sütun tek parça girer — etiket, başlık ve giriş bölünmez. */
const asideV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.06 } },
};

/* Sahnenin kendisi bir şey animasyonlamıyor; çocuklarının sırasını tutuyor. */
const beatV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

/* Düğüm önce yerine oturuyor, hat ondan sonra iniyor — bağlantının yönü
   böylece okunuyor: nokta var, çizgi ondan çıkıyor. */
const dotV: Variants = {
  hidden: { opacity: 0, scale: 0.4 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.snap) },
};

const railV: Variants = {
  hidden: { scaleY: 0 },
  show: { scaleY: 1, transition: spring.soft },
};

const headV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const textV: Variants = {
  hidden: { opacity: 0, y: 8, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

/* Kapanış tek başına bir beat: dizinin dışında, ondan sonra geliyor. */
const closeV: Variants = {
  hidden: { opacity: 0, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    filter: 'blur(0px)',
    transition: { duration: dur.slow, ease: ease.out },
  },
};

const closeMarkV: Variants = {
  hidden: { scaleY: 0 },
  show: { scaleY: 1, transition: spring.default },
};

export function AboutStory() {
  const t = useTranslations('about.story');
  const uid = useId();

  return (
    <section className="story" aria-labelledby={`${uid}-title`}>
      {/* Yan sütunlar basılmıyor — .grid-item-8fr `grid-column: 2` ile kendi
          yerleşiyor. Görünen kenar .story-inner'ın dikey hatları; hizası
          hero'nunkiyle aynı. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="story-inner" variants={sectionV} initial={false} animate="show">
            <div className="story-layout">
              <motion.div className="story-aside" variants={asideV}>
                <p className="story-eyebrow">{t('eyebrow')}</p>
                <h2 className="story-title" id={`${uid}-title`}>
                  {t.rich('title', { hl })}
                </h2>
                <p className="story-lead">{t('lead')}</p>
              </motion.div>

              <motion.ol className="story-beats" variants={listV}>
                {BEATS.map((key, i) => (
                  <motion.li className="story-beat" key={key} variants={beatV}>
                    {/* Düğüm + hat tek bir sütunda. Hat son sahnede
                        basılmıyor: dizi orada bitiyor, boşluğa uzanan bir
                        çizgi "devamı var" derdi. */}
                    <motion.span className="story-beat-dot" aria-hidden="true" variants={dotV} />
                    {i < BEATS.length - 1 && (
                      <motion.span
                        className="story-beat-rail"
                        aria-hidden="true"
                        variants={railV}
                      />
                    )}

                    <motion.h3 className="story-beat-head" variants={headV}>
                      {/* Numara dekoratif: sıra zaten <ol>'un kendisinde.
                          Ekran okuyucu "bir, iki, üç" diye iki kez saymasın. */}
                      <span className="story-beat-index" aria-hidden="true">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="story-beat-title">{t(`beats.${key}.title`)}</span>
                    </motion.h3>

                    <motion.p className="story-beat-text" variants={textV}>
                      {t(`beats.${key}.text`)}
                    </motion.p>
                  </motion.li>
                ))}
              </motion.ol>

              {/* Izgaranın ÜÇÜNCÜ çocuğu, listenin kardeşi değil: ≥900px'te
                  `grid-column: 2` ile sahnelerin tam altına oturuyor
                  (globals.css). Dışarı alınsaydı iki sütunun tamamına yayılır,
                  başlığın altından başlardı. */}
              <motion.p className="story-close" variants={closeV}>
                <motion.span
                  className="story-close-mark"
                  aria-hidden="true"
                  variants={closeMarkV}
                />
                {t('close')}
              </motion.p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
